import React, { createContext, useState, useContext, useEffect } from 'react';
import { removeLegacyAuthCookies } from '../utils/authUtils';
import { getKajabiUserData } from '../services/kajabiService';
import { hasKajabiAccess, getUserKajabiPlans } from '../utils/kajabiAccess';

import { useNavigate } from "react-router-dom";
import { supabase } from '../services/Supabase';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [userMetaData, setUserMetaData] = useState([]);
    const [userTheme, setUserTheme] = useState('ari-theme');
    const [categoryLists, setCategoryLists] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [loadingCatMsg, setLoadingCatMsg] = useState(true);
    const [hasARIAccess, setHasARIAccess] = useState(false);
    const [myKajabiPlans, setMyKajabiPlans] = useState('Upgrade Required');

    useEffect(() => {
        // Check if user is authenticated
        let isMounted = true;
        let subscription;

        async function initializeAuth() {
            
            // Get common random questions
            try {
                await getQuesList();
                // Get initial session
                const { data: { session: initialSession } } = await supabase.auth.getSession();
                if (isMounted) {
                    setUser(initialSession?.user ?? null);

                    // // Fetch user data from users table if session exists
                    if (initialSession?.user?.id) {
                        const userMetaData = await fetchUserMetaData(initialSession.user.id);
                        if (userMetaData && isMounted) {
                            const catLists = await commonCategoryList();
                            setCategoryLists(catLists);
                            setUserMetaData(userMetaData);
                            setUserTheme(userMetaData?.user_theme ?? 'ari-theme');

                            // Kajabi credentials and tokens remain in the serverless API.
                            // The API derives the email from the verified Supabase session.
                            const kajabiData = await getKajabiUserData(initialSession.access_token);
                        
                            if (kajabiData) {
                                await saveKajabiData(initialSession.user.id, kajabiData); // store in supabase
                                const hasAccess = hasKajabiAccess(kajabiData.offers); // check membership access
                                const userPlans = getUserKajabiPlans(kajabiData.offers); // get user plans
                                setMyKajabiPlans(userPlans);
                                setHasARIAccess((!hasAccess) ? false : true); // fallback to ARI access flag in metadata
                            }else{
                                setHasARIAccess(false);
                            }
                            setLoading(false);
                        }
                    }
                    setLoading(false);
                }

                // Set up auth state change listener
                const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
                    async (_event, newSession) => {
                        if (isMounted) {
                            setUser(newSession?.user ?? null);
                            // Fetch user data from users table if session exists
                            if (newSession?.user?.id) {
                                const userMetaData = await fetchUserMetaData(newSession.user.id);
                                if (userMetaData && isMounted) {
                                    const catLists = await commonCategoryList();
                                    setCategoryLists(catLists);
                                    setUserMetaData(userMetaData);
                                    setUserTheme(userMetaData?.user_theme ?? 'ari-theme');
                                }

                                // Run Kajabi sync through the authenticated serverless API.
                                const kajabiData = await getKajabiUserData(newSession.access_token);
                                
                                if (kajabiData) {
                                    await saveKajabiData(newSession.user.id, kajabiData); // store JSON
                                    const hasAccess = hasKajabiAccess(kajabiData.offers); // check access
                                    setHasARIAccess((!hasAccess) ? false : true);
                                    const userPlans = getUserKajabiPlans(kajabiData.offers);
                                    setMyKajabiPlans(userPlans);
                                }else{
                                    setHasARIAccess(false);
                                }
                            }
                            setLoading(false);
                        }
                    }
                );
                
                subscription = authSubscription;
            } catch (error) {
                console.error('Auth initialization error:', error);
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        initializeAuth();

        // Cleanup function
        return () => {
            isMounted = false;
            if (subscription) {
                subscription.unsubscribe();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // New Actions
    const loginAction = async (formData) => {
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email: formData.email,
                password: formData.password,
            });

            if (!error) {
                // Fetch user data from users table
                const userMetaData = await fetchUserMetaData(data.user.id);
                
                if (userMetaData) {
                    setUserMetaData(userMetaData);
                    setUser(data.user);
                    let fname = data.user.user_metadata.first_name ?? formData.email;
                    toast.success(`Welcome to ARI, ${fname}!`);
                    navigate("/dashboard");
                } else {
                    setUserMetaData([]);
                    setUser([]);
                    removeLegacyAuthCookies();
                    navigate("/sign-in");
                }
                
                return true;
            } else {
                toast.error(error.message);
                return false;
            }
        } catch (error) {
            toast.error(error.message);
            return false;
        }
    };

    const logOut = async (type) => {
        try {
            const { error } = await supabase.auth.signOut();
            if (!error) {
                removeLegacyAuthCookies();
                setUser(null);
                setCategoryLists([]);
                setUserMetaData([]);
                setUserTheme('ari-theme');
                if(type === 'passChange'){
                    toast.success('Password updated successfully! Please log in with your new password.');
                }else{
                    toast.success('You are now logged out!');
                }
                navigate("/sign-in");
            } else {
                toast.error('Error signing out:', error.message);
            }
        } catch (error) {
            toast.error('Error signing out:', error.message);
        }
    };

    const fetchUserMetaData = async (id) => {
        try {
            const { data: userMetaData, error } = await supabase
                .from('users_metadata')
                .select('*')
                .eq('user_id', id)
                .single();

            if (error !== null) {
                console.error('Error fetching user metadata:', error);
                return null;
            }
            return userMetaData;
        } catch (error) {
            console.error('Error fetching user metadata:', error);
            return null;
        }
    };

    const commonCategoryList = async () => {
        // eslint-disable-next-line
        let { data: coaching_categories, error } = await supabase.from('coaching_categories').select('*').order('order', { ascending: true });
        return coaching_categories;
    };

    const commonQuestionsList = async () => {
        // eslint-disable-next-line
        let { data: common_questions, error } = await supabase.from('common_questions').select('*, category_id(category_name)');
        return common_questions;
    };

    const getQuesList = async () => {
        const questList = await commonQuestionsList();
        const selectRandomQuestions = () => {
            let shuffled = [...questList].sort(() => 0.5 - Math.random());
            return shuffled.slice(0, 5);
        };
        if(selectedQuestions.length === 0){
            setSelectedQuestions(selectRandomQuestions());
        }
    };

    const saveKajabiData = async (userId, kajabiData) => {
        // eslint-disable-next-line
        const { data, error } = await supabase
        .from('users_metadata')
            .update({
                kajabi_data: kajabiData, // store full JSON
            })
            .eq('user_id', userId)
        .select().maybeSingle();
    };

    const value = {
        user, setUser,
        logOut, loginAction,
        loading, setLoading,
        userMetaData, setUserMetaData, fetchUserMetaData,
        userTheme, setUserTheme,
        categoryLists, selectedQuestions,
        loadingCatMsg, setLoadingCatMsg,
        saveKajabiData, hasARIAccess,
        myKajabiPlans, commonCategoryList
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
